import React, { useEffect, useState } from 'react';
import { useUserData } from '../../../hooks/useUserData';
import { emailPreferencesService, EmailPreference } from '../../../services/emailPreferencesService';

const SettingsPage: React.FC = () => {
  const { user, isLoading } = useUserData();
  const [emailPreferences, setEmailPreferences] = useState({
    hourly: true,
    daily: false,
  });
  const [hasUserEditedPreferences, setHasUserEditedPreferences] = useState(false);
  const [isSavingEmailPreferences, setIsSavingEmailPreferences] = useState(false);
  const [emailStatus, setEmailStatus] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);

  const userId = user?.sub;
  const emailPreferenceStorageKey = userId ? `sinalinvest.emailPreferences.${userId}` : null;
  const botBaseUrl = 'https://t.me/SinalInvestBrBot';
  const telegramLink = userId ? `${botBaseUrl}?start=${userId}` : botBaseUrl;

  const handleCopyTelegramLink = async () => {
    try {
      await navigator.clipboard.writeText(telegramLink);
      setCopyStatus('Link copiado!');
    } catch (error) {
      console.error('Erro ao copiar link:', error);
      setCopyStatus('Não foi possível copiar o link.');
    } finally {
      setTimeout(() => setCopyStatus(null), 2500);
    }
  };

  const getEmailPreferenceValue = (): EmailPreference => {
    if (emailPreferences.hourly && emailPreferences.daily) return 'TODOS';
    if (emailPreferences.hourly) return 'HORA';
    if (emailPreferences.daily) return 'DIARIO';
    return 'NENHUM';
  };

  const isValidEmailPreference = (value: string): value is EmailPreference =>
    ['TODOS', 'DIARIO', 'HORA', 'NENHUM'].includes(value);

  const applyEmailPreferenceValue = (value: EmailPreference) => {
    switch (value) {
      case 'TODOS':
        setEmailPreferences({ hourly: true, daily: true });
        break;
      case 'HORA':
        setEmailPreferences({ hourly: true, daily: false });
        break;
      case 'DIARIO':
        setEmailPreferences({ hourly: false, daily: true });
        break;
      case 'NENHUM':
      default:
        setEmailPreferences({ hourly: false, daily: false });
        break;
    }
  };

  useEffect(() => {
    if (!user || hasUserEditedPreferences) {
      return;
    }

    if (emailPreferenceStorageKey) {
      const storedPreference = localStorage.getItem(emailPreferenceStorageKey);
      if (storedPreference && isValidEmailPreference(storedPreference)) {
        applyEmailPreferenceValue(storedPreference);
        setHasUserEditedPreferences(true);
        return;
      }
    }

    const rawPreference =
      (user.fullProfile as Record<string, unknown>)?.['custom:notificacao_email'] ??
      (user.fullIdTokenClaims as Record<string, unknown>)?.['custom:notificacao_email'];

    if (typeof rawPreference === 'string') {
      applyEmailPreferenceValue(rawPreference as EmailPreference);
    }
  }, [user, hasUserEditedPreferences, emailPreferenceStorageKey]);

  const handleSaveEmailPreference = async () => {
    setIsSavingEmailPreferences(true);
    const preferenceValue = getEmailPreferenceValue();
    const response = await emailPreferencesService.updateEmailPreferences(preferenceValue);

    if (response.ok) {
      const appliedPreference = response.data?.emailPreferences || preferenceValue;
      applyEmailPreferenceValue(appliedPreference);
      if (emailPreferenceStorageKey) {
        localStorage.setItem(emailPreferenceStorageKey, appliedPreference);
      }
      setHasUserEditedPreferences(true);
      setEmailStatus('Preferência de alerta salva com sucesso!');
    } else {
      setEmailStatus('Não foi possível salvar a preferência.');
    }

    setIsSavingEmailPreferences(false);
    setTimeout(() => setEmailStatus(null), 2500);
  };

  return (
    <div className="p-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Configurações</h1>
        <p className="text-lg text-gray-600">Ajuste seus alertas e integrações.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Bot do Telegram</h2>
              <p className="text-gray-600 mt-1">
                Inscreva-se no Telegram usando o link abaixo.
              </p>
            </div>
            <div className="text-2xl">🤖</div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Seu link de inscrição</label>
              <input
                type="text"
                value={telegramLink}
                readOnly
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-700"
              />
              <p className="text-xs text-gray-500 mt-2">
                {isLoading
                  ? 'Carregando seu ID...'
                  : userId
                    ? 'Use este link para iniciar a integração e vincular sua conta.'
                    : 'Seu ID não está disponível no momento.'}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={telegramLink}
                target="_blank"
                rel="noreferrer"
                className={`px-4 py-2 rounded-lg font-semibold text-white text-center transition-colors ${
                  userId ? 'bg-blue-700 hover:bg-blue-800' : 'bg-gray-300 cursor-not-allowed'
                }`}
                aria-disabled={!userId}
                onClick={(event) => {
                  if (!userId) {
                    event.preventDefault();
                  }
                }}
              >
                Abrir no Telegram
              </a>
              <button
                type="button"
                onClick={handleCopyTelegramLink}
                className="px-4 py-2 rounded-lg font-semibold text-blue-700 border border-blue-700 hover:bg-blue-50 transition-colors"
              >
                Copiar link
              </button>
            </div>
            {copyStatus && <p className="text-sm text-green-600">{copyStatus}</p>}
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Alertas por email</h2>
              <p className="text-gray-600 mt-1">
                Defina com que frequência você deseja receber os alertas.
              </p>
            </div>
            <div className="text-2xl">✉️</div>
          </div>

          <div className="space-y-4">
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-700 transition-colors">
                <input
                  type="checkbox"
                  name="email-hourly"
                  checked={emailPreferences.hourly}
                  onChange={(event) => {
                    setHasUserEditedPreferences(true);
                    setEmailPreferences(prev => ({
                      ...prev,
                      hourly: event.target.checked,
                    }));
                  }}
                  className="text-blue-700"
                />
                <div>
                  <p className="font-medium text-gray-900">A cada hora</p>
                  <p className="text-sm text-gray-600">Resumo horário com as principais notícias.</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-700 transition-colors">
                <input
                  type="checkbox"
                  name="email-daily"
                  checked={emailPreferences.daily}
                  onChange={(event) => {
                    setHasUserEditedPreferences(true);
                    setEmailPreferences(prev => ({
                      ...prev,
                      daily: event.target.checked,
                    }));
                  }}
                  className="text-blue-700"
                />
                <div>
                  <p className="font-medium text-gray-900">Diário</p>
                  <p className="text-sm text-gray-600">Resumo diário com as principais notícias.</p>
                </div>
              </label>
            </div>

            <button
              type="button"
              onClick={handleSaveEmailPreference}
              className="w-full sm:w-auto px-4 py-2 rounded-lg font-semibold text-white bg-blue-700 hover:bg-blue-800 transition-colors disabled:cursor-not-allowed disabled:opacity-70"
              disabled={isSavingEmailPreferences}
            >
              {isSavingEmailPreferences ? 'Salvando...' : 'Salvar preferência'}
            </button>
            {emailStatus && (
              <p className={`text-sm ${emailStatus.includes('Não') ? 'text-red-600' : 'text-green-600'}`}>
                {emailStatus}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
